import { GetEmspUsersInvoicesPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/emsp-users-invoices/get-emsp-users-invoices-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { EmspUserInvoiceBaseModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/invoices-service-lib/src/wrapper/outputs/emsp-user-invoice-base.model';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  EMSP_BO_INVOICE_FILTERS_LSK,
  EMSP_BO_INVOICE_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  EMSP_BO_INVOICE_LIST_DEFAULT_OPTIONS,
  EMSP_BO_INVOICE_LIST_FILTER_NAME_LIST,
  EMSP_BO_INVOICE_LIST_SORT_FIELD,
  EMSP_BO_INVOICE_LIST_SORT_ORDER,
  EmspBoInvoiceListOptionsModel,
} from './emsp-bo-invoice-list/emsp-bo-invoice-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const EmspBoInvoiceContext =
  createContext<EmspUserInvoiceBaseModel | null>(null);

export const useEmspBoInvoiceContext = (): EmspUserInvoiceBaseModel => {
  const context = useContext(EmspBoInvoiceContext);

  if (!context) {
    throw new Error(
      'useEmspBoInvoiceContext must be used within EmspBoInvoiceContext.Provider',
    );
  }

  return context;
};

interface EmspBoInvoiceContextProviderProps {
  data: EmspUserInvoiceBaseModel;
  children: React.ReactNode;
}

export const EmspBoInvoiceContextProvider = ({
  data,
  children,
}: EmspBoInvoiceContextProviderProps): React.JSX.Element => {
  return (
    <EmspBoInvoiceContext.Provider value={data}>
      {children}
    </EmspBoInvoiceContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface EmspBoInvoiceListContextProps {
  getPaginatedQueryParameters: GetEmspUsersInvoicesPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetEmspUsersInvoicesPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  columnOptions: EmspBoInvoiceListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<EmspBoInvoiceListOptionsModel>>;
}

export const EmspBoInvoiceListContext =
  createContext<EmspBoInvoiceListContextProps | null>(null);

export const useEmspBoInvoiceListContext =
  (): EmspBoInvoiceListContextProps => {
    const context = useContext(EmspBoInvoiceListContext);

    if (!context) {
      throw new Error(
        'useEmspBoInvoiceListContext must be used within EmspBoInvoiceListContext.Provider',
      );
    }

    return context;
  };

interface EmspBoInvoiceListContextProviderProps {
  children: React.ReactNode;
}

export const EmspBoInvoiceListContextProvider = ({
  children,
}: EmspBoInvoiceListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetEmspUsersInvoicesPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        status: [],
        type: [],
        textToSearch: '',
        // Sortable fields
        sortField: EMSP_BO_INVOICE_LIST_SORT_FIELD,
        sortOrder: EMSP_BO_INVOICE_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...EMSP_BO_INVOICE_LIST_FILTER_NAME_LIST,
      ],
      EMSP_BO_INVOICE_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<EmspBoInvoiceListOptionsModel>(
      EMSP_BO_INVOICE_LIST_DEFAULT_OPTIONS,
      EMSP_BO_INVOICE_LIST_OPTIONS_LSK,
    );

  const data = useMemo(
    () => ({
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      columnOptions,
      setColumnOptions,
    }),
    [
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      columnOptions,
      setColumnOptions,
    ],
  );

  return (
    <EmspBoInvoiceListContext.Provider value={data}>
      {children}
    </EmspBoInvoiceListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterEmspBoInvoiceListContext =
  createContext<DeepPartialSkipArrayKey<GetEmspUsersInvoicesPaginatedModel> | null>(
    null,
  );

export const useFilterEmspBoInvoiceListContext =
  (): DeepPartialSkipArrayKey<GetEmspUsersInvoicesPaginatedModel> => {
    const context = useContext(FilterEmspBoInvoiceListContext);

    if (!context) {
      throw new Error(
        'useFilterEmspBoInvoiceListContext must be used within FilterEmspBoInvoiceListContext.Provider',
      );
    }

    return context;
  };

interface FilterEmspBoInvoiceListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetEmspUsersInvoicesPaginatedModel>;
  children: React.ReactNode;
}

export const FilterEmspBoInvoiceListContextProvider = ({
  data,
  children,
}: FilterEmspBoInvoiceListContextProviderProps): React.JSX.Element => {
  return (
    <FilterEmspBoInvoiceListContext.Provider value={data}>
      {children}
    </FilterEmspBoInvoiceListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchEmspBoInvoiceListContext =
  createContext<DeepPartialSkipArrayKey<GetEmspUsersInvoicesPaginatedModel> | null>(
    null,
  );

export const useFilterSearchEmspBoInvoiceListContext =
  (): DeepPartialSkipArrayKey<GetEmspUsersInvoicesPaginatedModel> => {
    const context = useContext(FilterSearchEmspBoInvoiceListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchEmspBoInvoiceListContext must be used within FilterSearchEmspBoInvoiceListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchEmspBoInvoiceListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetEmspUsersInvoicesPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchEmspBoInvoiceListContextProvider = ({
  data,
  children,
}: FilterSearchEmspBoInvoiceListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchEmspBoInvoiceListContext.Provider value={data}>
      {children}
    </FilterSearchEmspBoInvoiceListContext.Provider>
  );
};
